import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { catalogTaskIndex } from '../../ProfilePages/components/quoteCatalog';
import { formatAUD } from '../../../utils/formatCurrency';
import {
  getLocalEmailSignature,
  isBlankSignatureHtml,
} from '../../ProfilePages/components/emailSignatureStorage';
import { useProfileRefresh } from '../../ProfilePages/ProfileBookingContext';
import QuotationServicePicker from '../../ProfilePages/components/QuotationServicePicker';
import { invoiceEmailDraft } from '../../ProfilePages/components/QuotationSendEmail';
import '../../ProfilePages/styles/Invoice.css';
import '../../ProfilePages/styles/QuotationForm.css';
import '../styles/ProjectDetailView.css';

const FIND_PROJECT_INVOICES = `
  query FindBookingInvoice($bookingId: String!, $projectId: String) {
    findBookingInvoice(bookingId: $bookingId, projectId: $projectId) {
      id
      invoiceNumber
      invoiceStage
      paymentStatus
      listTotal
      listInvoiceKind
      listCustomerEmail
      createdAt
    }
  }
`;

const CREATE_CUSTOM_INVOICE = `
  mutation CreateCustomInvoiceForProject($input: CreateCustomInvoiceInput!) {
    createCustomInvoiceForProject(input: $input) {
      id
      invoiceNumber
      invoiceStage
      listTotal
      listInvoiceKind
    }
  }
`;

const SEND_INVOICE_EMAIL = `
  mutation SendMailInvoice($bookingId: String!, $info: String!, $invoiceId: String) {
    sendMailInvoice(
      bookingId: $bookingId,
      info: $info,
      invoiceId: $invoiceId
    )
  }
`;

const FIND_ONE_INVOICE = `
  query FindOneBookingInvoice($id: String!) {
    findOneBookingInvoice(id: $id) {
      id
      invoiceNumber
      invoiceStage
      createdAt
      rawData
    }
  }
`;

const FIND_PROJECT_BILLING = `
  query CustomInvoiceProject($id: String!) {
    findOneProject(id: $id) {
      id
      projectId
      name
      seriesRootId
      startDate
      deadLine
      attention
      projectAddress
      deliveryAddress
      purchaseOrderNumber
      visitExtras
      invoiceSettings
      projectServices
    }
  }
`;

const SERVICE_TASK_TEXT = `
  query CustomInvoiceServiceText {
    getService(includeStats: false) {
      id
      service
      catalog
    }
  }
`;

const FIND_TASKS_BY_PROJECT = `
  query FindTasksByProject($projectId: String!) {
    findTasksByProject(projectId: $projectId) {
      id
      title
      description
      remainder
    }
  }
`;

const FIND_INVOICE_CLIENT = `
  query FindInvoiceClient($id: String!) {
    findOneBooking(id: $id) {
      id
      fullName
      email
      phone
      address
      postcode
    }
  }
`;

const GET_COMPANY_INVOICE_BANK = `
  query GetCompanyInvoiceBank {
    getMyProfile {
      company {
        invoiceBankName
        invoiceAccountName
        invoiceBsb
        invoiceAccountNumber
        invoiceBankReference
      }
    }
  }
`;

function todayInput() {
  return toDateInputValue(new Date());
}

function emptyForm() {
  const invoiceDate = todayInput();
  return {
    title: '',
    description: '',
    amount: '',
    invoiceType: 'STANDARD',
    invoiceDate,
    dueDate: addDaysToDateInput(invoiceDate, 15),
    serviceDate: '',
    serviceEndDate: '',
    paymentTermsDays: '15',
    projectReference: '',
    projectName: '',
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    clientAddress: '',
    attention: '',
    projectAddress: '',
    deliveryAddress: '',
    purchaseOrderNumber: '',
    bankName: '',
    accountName: '',
    bsb: '',
    accountNumber: '',
    bankReference: '',
    lines: [],
  };
}

function taskServiceKey(task) {
  const remainder = String(task?.remainder || '').trim();
  if (remainder.startsWith('svc:')) return remainder.slice(4);
  const match = String(task?.description || '').match(/^Service:([0-9a-f-]{36})\s*$/im);
  return match ? match[1] : '';
}

function labeledLine(description, label) {
  const match = String(description || '').match(
    new RegExp(`^${label}:\\s*(.+)$`, 'im'),
  );
  return String(match?.[1] || '').trim();
}

function taskNotes(description) {
  return String(description || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(
      (line) =>
        line &&
        !/^Service:/i.test(line) &&
        !/^Requirement:/i.test(line) &&
        !/^Role:/i.test(line) &&
        !/^Frequency:/i.test(line) &&
        !/^Group:/i.test(line) &&
        !/^Client-facing scope:/i.test(line) &&
        !/^Internal work instructions:/i.test(line) &&
        !/^Source:/i.test(line),
    )
    .join('\n');
}

function taskFacing(task) {
  const description = String(task?.description || '');
  const title = String(task?.title || task?.taskTitle || '').trim();
  const notes = taskNotes(description);
  const scope = String(
    task?.scope || labeledLine(description, 'Client-facing scope'),
  ).trim();
  return {
    groupName: String(
      task?.groupName || labeledLine(description, 'Group'),
    ).trim(),
    scope:
      scope ||
      (notes && notes.toLowerCase() !== title.toLowerCase() ? notes : ''),
    instructions: String(
      task?.instructions ||
        labeledLine(description, 'Internal work instructions'),
    ).trim(),
  };
}

function extraNotes(project, hasRegularInvoice) {
  if (!hasRegularInvoice) return new Set();
  const notes = new Set();
  (Array.isArray(project?.visitExtras) ? project.visitExtras : []).forEach((row) => {
    const note = String(row?.note || '').trim().toLowerCase();
    if (note && Number(row?.amount) > 0) notes.add(note);
  });
  return notes;
}

function catalogForService(catalogById, serviceId, serviceName) {
  const id = String(serviceId || '').trim();
  if (id && catalogById?.[id]) return catalogById[id];
  const name = String(serviceName || '').trim().toLowerCase();
  if (!name) return null;
  return (
    Object.values(catalogById || {}).find(
      (service) => String(service?.service || '').trim().toLowerCase() === name,
    ) || null
  );
}

function withCatalogFacing(task, service) {
  if (task.scope && task.instructions && task.groupName) return task;
  const index = service ? catalogTaskIndex(service) : null;
  const match = index?.byTitle?.get(String(task.title || '').trim().toLowerCase());
  if (!match) return task;
  return {
    ...task,
    groupName: task.groupName || match.groupName || '',
    scope: task.scope || match.scope || '',
    instructions: task.instructions || match.instructions || '',
  };
}

function linesFromProject(project, taskRows, skipNotes, catalogById) {
  const services = Array.isArray(project?.projectServices) ? project.projectServices : [];
  const tasks = Array.isArray(taskRows) ? taskRows : [];
  const describe = (planned, serviceId) => {
    const title = String(planned?.title || planned?.name || '').trim();
    const match = tasks.find((row) => {
      const sameTitle = String(row?.title || '').trim().toLowerCase() === title.toLowerCase();
      const sameService = !serviceId || taskServiceKey(row) === serviceId;
      return sameTitle && sameService;
    });
    const plannedFacing = taskFacing(planned);
    const liveFacing = taskFacing(match);
    return {
      taskId: String(match?.id || planned?.taskId || planned?.id || '').trim(),
      title,
      groupName: plannedFacing.groupName || liveFacing.groupName,
      scope: plannedFacing.scope || liveFacing.scope,
      instructions: plannedFacing.instructions || liveFacing.instructions,
      description: taskNotes(planned?.description || match?.description || ''),
      price: '',
    };
  };
  const lines = services
    .map((row) => {
      const serviceId = String(row?.service?.id || row?.catalogServiceId || '').trim();
      const serviceName = String(
        row?.service?.service || row?.serviceName || row?.name || '',
      ).trim();
      if (!serviceName && !serviceId) return null;
      const planned = (Array.isArray(row?.tasks) ? row.tasks : []).filter((task) =>
        String(task?.title || '').trim(),
      );
      const tagged = tasks.filter((task) => serviceId && taskServiceKey(task) === serviceId);
      const source = planned.length ? planned : tagged;
      const catalog = catalogForService(catalogById, serviceId, serviceName);
      return {
        serviceId,
        serviceName: serviceName || 'Service',
        tasks: source
        .map((task) => withCatalogFacing(describe(task, serviceId), catalog))
        .filter(
          (task) =>
            task.title &&
            !skipNotes?.has(String(task.title).trim().toLowerCase()),
        ),
      };
    })
    .filter(Boolean);
  if (lines.length) return lines;
  if (!tasks.length) return [];
  return [
    {
      serviceId: '',
      serviceName: 'Project tasks',
      tasks: tasks
        .map((task) => withCatalogFacing(describe(task, ''), null))
        .filter(
          (task) =>
            task.title &&
            !skipNotes?.has(String(task.title).trim().toLowerCase()),
        ),
    },
  ];
}

function taskPriceTotal(lines) {
  return (lines || []).reduce(
    (sum, line) =>
      sum +
      (line.tasks || []).reduce(
        (taskSum, task) => taskSum + (Number(task.price) || 0),
        0,
      ),
    0,
  );
}

function billingFromProject(project) {
  if (!project) return {};
  return {
    serviceDate: toDateInputValue(project.startDate),
    serviceEndDate: toDateInputValue(project.deadLine),
    attention: String(project.attention || '').trim(),
    projectAddress: String(project.projectAddress || '').trim(),
    deliveryAddress: String(project.deliveryAddress || '').trim(),
    purchaseOrderNumber: String(project.purchaseOrderNumber || '').trim(),
  };
}

async function loadProjectDefaults(id, bookingId) {
  if (!id) return {};
  try {
    const [res, taskRes, catalogRes] = await Promise.all([
      graphqlRequest(FIND_PROJECT_BILLING, { id }),
      graphqlRequest(FIND_TASKS_BY_PROJECT, { projectId: id }).catch(() => ({})),
      graphqlRequest(SERVICE_TASK_TEXT).catch(() => ({})),
    ]);
    const catalogById = {};
    (catalogRes?.getService || []).forEach((row) => {
      if (row?.id) catalogById[row.id] = row;
    });
    let project = res?.findOneProject || null;
    const rootId = String(project?.seriesRootId || '').trim();
    if (rootId) {
      const rootRes = await graphqlRequest(FIND_PROJECT_BILLING, { id: rootId }).catch(
        () => ({}),
      );
      const root = rootRes?.findOneProject || {};
      project = {
        ...root,
        ...project,
        startDate: project?.startDate || root.startDate,
        deadLine: project?.deadLine || root.deadLine,
        attention: project?.attention || root.attention,
        projectAddress: project?.projectAddress || root.projectAddress,
        deliveryAddress: project?.deliveryAddress || root.deliveryAddress,
        purchaseOrderNumber:
          project?.purchaseOrderNumber || root.purchaseOrderNumber,
        projectServices: project?.projectServices?.length
          ? project.projectServices
          : root.projectServices,
      };
    }
    const taskRows = Array.isArray(taskRes?.findTasksByProject)
      ? taskRes.findTasksByProject
      : [];
    let hasRegularInvoice = false;
    if (bookingId) {
      const invoiceRes = await graphqlRequest(FIND_PROJECT_INVOICES, {
        bookingId,
        projectId: id,
      }).catch(() => ({}));
      const rows = Array.isArray(invoiceRes?.findBookingInvoice)
        ? invoiceRes.findBookingInvoice
        : [];
      hasRegularInvoice = rows.some(
        (row) => String(row?.listInvoiceKind || '').toUpperCase() !== 'CUSTOM',
      );
    }
    const dueDays = Number(project?.invoiceSettings?.dueDays);
    const invoicedExtras = hasRegularInvoice
      ? (Array.isArray(project?.visitExtras) ? project.visitExtras : [])
          .map((row) => ({
            note: String(row?.note || '').trim(),
            amount: Number(row?.amount) || 0,
          }))
          .filter((row) => row.note && row.amount > 0)
      : [];
    return {
      ...billingFromProject(project),
      projectReference: String(project?.projectId || '').trim(),
      projectName: String(project?.name || '').trim(),
      paymentTermsDays: Number.isFinite(dueDays) ? String(dueDays) : '15',
      invoicedExtras,
      lines: linesFromProject(
        project,
        taskRows,
        extraNotes(project, hasRegularInvoice),
        catalogById,
      ),
    };
  } catch {
    return {};
  }
}

function toDateInputValue(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

async function loadClientDetails(bookingId) {
  if (!bookingId) return {};
  try {
    const res = await graphqlRequest(FIND_INVOICE_CLIENT, { id: bookingId });
    const booking = res?.findOneBooking || {};
    const address = [booking.address, booking.postcode]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(', ');
    return {
      clientName: String(booking.fullName || '').trim(),
      clientPhone: String(booking.phone || '').trim(),
      clientEmail: String(booking.email || '').trim(),
      clientAddress: address,
    };
  } catch {
    return {};
  }
}

function daysBetweenInputs(from, to) {
  const start = toDateInputValue(from);
  const end = toDateInputValue(to);
  if (!start || !end) return '';
  const a = Date.UTC(
    Number(start.slice(0, 4)),
    Number(start.slice(5, 7)) - 1,
    Number(start.slice(8, 10)),
  );
  const b = Date.UTC(
    Number(end.slice(0, 4)),
    Number(end.slice(5, 7)) - 1,
    Number(end.slice(8, 10)),
  );
  const days = Math.round((b - a) / 86400000);
  return days >= 0 ? String(days) : '';
}

function addDaysToDateInput(value, days) {
  const iso = toDateInputValue(value);
  const base = iso
    ? new Date(
        Number(iso.slice(0, 4)),
        Number(iso.slice(5, 7)) - 1,
        Number(iso.slice(8, 10)),
      )
    : new Date();
  const next = new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate() + days,
  );
  return toDateInputValue(next);
}

export default function CustomInvoicePanel({
  bookingId,
  projectId,
  projects = [],
  clientEmail = '',
  variant = 'full',
}) {
  const { bumpRefresh } = useProfileRefresh();
  const createOnly = variant === 'create-only';
  const [customInvoices, setCustomInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showCatalogPicker, setShowCatalogPicker] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [chosenProjectId, setChosenProjectId] = useState(projectId || '');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const invoicedExtrasRef = useRef([]);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailNote, setEmailNote] = useState({
    to: '',
    subject: '',
    message: '',
    cc: '',
    dueDate: '',
  });
  const [sending, setSending] = useState(false);

  const fetchCustomInvoices = useCallback(async () => {
    if (!bookingId || !projectId) {
      setCustomInvoices([]);
      return;
    }
    try {
      const res = await graphqlRequest(FIND_PROJECT_INVOICES, {
        bookingId,
        projectId,
      });
      const rows = Array.isArray(res?.findBookingInvoice)
        ? res.findBookingInvoice
        : [];
      setCustomInvoices(
        rows.filter(
          (row) =>
            String(row?.listInvoiceKind || '').toUpperCase() === 'CUSTOM',
        ),
      );
    } catch (err) {
      console.error(err);
      setCustomInvoices([]);
    }
  }, [bookingId, projectId]);

  useEffect(() => {
    if (createOnly) return;
    fetchCustomInvoices();
  }, [createOnly, fetchCustomInvoices]);

  const applyProjectDefaults = async (id) => {
    const [billing, client] = await Promise.all([
      loadProjectDefaults(id, bookingId),
      loadClientDetails(bookingId),
    ]);
    invoicedExtrasRef.current = billing.invoicedExtras || [];
    const days = Number(billing.paymentTermsDays);
    setForm((prev) => ({
      ...prev,
      serviceDate: billing.serviceDate || '',
      serviceEndDate: billing.serviceEndDate || '',
      attention: billing.attention || '',
      projectAddress: billing.projectAddress || '',
      deliveryAddress: billing.deliveryAddress || '',
      purchaseOrderNumber: billing.purchaseOrderNumber || '',
      projectReference: billing.projectReference || '',
      projectName: billing.projectName || '',
      paymentTermsDays: billing.paymentTermsDays || '15',
      dueDate: addDaysToDateInput(
        prev.invoiceDate,
        Number.isFinite(days) ? days : 15,
      ),
      lines: Array.isArray(billing.lines) ? billing.lines : [],
      clientName: prev.clientName || client.clientName || '',
      clientPhone: prev.clientPhone || client.clientPhone || '',
      clientEmail: prev.clientEmail || client.clientEmail || clientEmail || '',
      clientAddress: prev.clientAddress || client.clientAddress || '',
    }));
  };

  const openForm = (invoice = null) => {
    const nextProject = projectId || projects[0]?.id || '';
    setEditing(invoice);
    setChosenProjectId(nextProject);
    setForm(emptyForm());
    setShowForm(true);
    if (invoice) return;
    applyProjectDefaults(nextProject);
    graphqlRequest(GET_COMPANY_INVOICE_BANK)
      .then((bankRes) => {
        const company = bankRes?.getMyProfile?.company || {};
        setForm((prev) => ({
          ...prev,
          bankName: prev.bankName || String(company.invoiceBankName || '').trim(),
          accountName:
            prev.accountName || String(company.invoiceAccountName || '').trim(),
          bsb: prev.bsb || String(company.invoiceBsb || '').trim(),
          accountNumber:
            prev.accountNumber || String(company.invoiceAccountNumber || '').trim(),
          bankReference:
            prev.bankReference || String(company.invoiceBankReference || '').trim(),
        }));
      })
      .catch(() => {});
  };

  const loadForEdit = async (invoice) => {
    if (!invoice?.id) {
      openForm(null);
      return;
    }
    try {
      const res = await graphqlRequest(FIND_ONE_INVOICE, { id: invoice.id });
      const detail = res?.findOneBookingInvoice;
      const raw = detail?.rawData || {};
      const group = raw?.services?.groups?.[0] || {};
      const bank = raw.bankDetails || {};
      const invoiceDate =
        toDateInputValue(raw.issuedAt || detail.createdAt) || todayInput();
      setEditing(invoice);
      setChosenProjectId(invoice.projectId || projectId || '');
      setForm({
        title: String(raw.customTitle || group.serviceName || '').trim(),
        description: String(
          raw.clientDescription || group.description || '',
        ).trim(),
        amount: String(
          Number(
            group.serviceTotal ??
              group.servicePrice ??
              raw.services?.totalPrice,
          ) || '',
        ),
        invoiceType: String(raw.invoiceType || 'STANDARD').toUpperCase(),
        invoiceDate,
        dueDate: toDateInputValue(raw.dueDate) || addDaysToDateInput(invoiceDate, 15),
        paymentTermsDays:
          String(raw.paymentTerms || '').match(/(\d+)/)?.[1] ||
          daysBetweenInputs(invoiceDate, raw.dueDate) ||
          '15',
        projectReference: String(raw.projectReference || '').trim(),
        projectName: String(raw.projectName || '').trim(),
        serviceDate: toDateInputValue(raw.serviceDate),
        serviceEndDate: toDateInputValue(raw.serviceEndDate),
        clientName: String(raw.bookingInfo?.fullName || '').trim(),
        clientPhone: String(raw.bookingInfo?.phone || '').trim(),
        clientEmail: String(raw.bookingInfo?.email || '').trim(),
        clientAddress: String(raw.bookingInfo?.address || '').trim(),
        attention: String(raw.attention || ''),
        projectAddress: String(raw.projectAddress || ''),
        deliveryAddress: String(raw.deliveryAddress || ''),
        purchaseOrderNumber: String(raw.purchaseOrderNumber || ''),
        bankName: String(bank.bankName || ''),
        accountName: String(bank.accountName || ''),
        bsb: String(bank.bsb || ''),
        accountNumber: String(bank.accountNumber || bank.bankNumber || ''),
        bankReference: String(bank.reference || ''),
        lines: (Array.isArray(raw?.services?.groups) ? raw.services.groups : []).map(
          (group) => ({
            serviceId: String(group?.serviceId || group?.service?.id || ''),
            serviceName: String(group?.serviceName || group?.service?.service || ''),
            tasks: (Array.isArray(group?.tasks) ? group.tasks : [])
              .map((task) => {
                const facing = taskFacing(task);
                return {
                  taskId: String(task?.taskId || ''),
                  title: String(task?.title || task?.taskTitle || '').trim(),
                  groupName: facing.groupName,
                  scope: facing.scope,
                  instructions: facing.instructions,
                  description: taskNotes(task?.description || task?.taskDescription || ''),
                  price:
                    Number(task?.price) || Number(task?.total)
                      ? String(Number(task?.price) || Number(task?.total))
                      : '',
                };
              })
              .filter((task) => task.title || task.scope || task.instructions),
          }),
        ),
      });
      setShowForm(true);
      const billing = await loadProjectDefaults(
        invoice.projectId || projectId,
        bookingId,
      );
      invoicedExtrasRef.current = billing.invoicedExtras || [];
      setForm((prev) => ({
        ...prev,
        projectReference: prev.projectReference || billing.projectReference || '',
        projectName: prev.projectName || billing.projectName || '',
      }));
      if (!String(raw.bookingInfo?.fullName || '').trim()) {
        const client = await loadClientDetails(bookingId);
        setForm((prev) => ({
          ...prev,
          clientName: prev.clientName || client.clientName || '',
          clientPhone: prev.clientPhone || client.clientPhone || '',
          clientEmail: prev.clientEmail || client.clientEmail || '',
          clientAddress: prev.clientAddress || client.clientAddress || '',
        }));
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to load custom invoice');
    }
  };

  const handleSave = async () => {
    const targetProjectId = chosenProjectId || projectId;
    if (savingRef.current) return;
    if (!bookingId || !targetProjectId) {
      toast.error('Select the project for this invoice.');
      return;
    }
    const title = String(form.title || '').trim();
    const linesToSend = (form.lines || [])
      .map((line) => ({
        serviceId: line.serviceId || '',
        serviceName: line.serviceName || '',
        tasks: (line.tasks || [])
          .filter(
            (task) =>
              String(task.title || '').trim() ||
              String(task.scope || '').trim() ||
              Number(task.price) > 0,
          )
          // No internal work instructions: an invoice is a client document.
          .map((task) => ({
            taskId: task.taskId || '',
            title: task.title || '',
            groupName: String(task.groupName || '').trim(),
            scope: task.scope || '',
            description: task.description || '',
            price: Number(task.price) || 0,
          })),
      }))
      .filter((line) => line.tasks.length);
    const pricedTotal = taskPriceTotal(linesToSend);
    const amount = pricedTotal > 0 ? pricedTotal : Number(form.amount);
    if (!title) {
      toast.error('Enter an invoice title');
      return;
    }
    if (
      linesToSend.some((line) =>
        line.tasks.some((task) => task.price > 0 && !String(task.title || '').trim()),
      )
    ) {
      toast.error('Each priced task needs a name');
      return;
    }
    if (!(amount > 0)) {
      toast.error('Enter an invoice amount, or a price on a task');
      return;
    }
    const blockedExtra = linesToSend
      .flatMap((line) => line.tasks)
      .find((task) =>
        invoicedExtrasRef.current.some(
          (extra) =>
            extra.note.toLowerCase() === String(task.title || '').trim().toLowerCase() &&
            Math.abs(extra.amount - task.price) < 0.01,
        ),
      );
    if (blockedExtra) {
      toast.error(
        `"${blockedExtra.title}" is already invoiced as an extra charge on this project.`,
      );
      return;
    }
    const termsDays = Math.max(0, Number(form.paymentTermsDays) || 0);
    savingRef.current = true;
    setSaving(true);
    try {
      const saved = await graphqlRequest(CREATE_CUSTOM_INVOICE, {
        input: {
          bookingId,
          projectId: targetProjectId,
          title,
          description: String(form.description || '').trim(),
          amount,
          invoiceType: form.invoiceType || 'STANDARD',
          invoiceId: editing?.id || null,
          invoiceDate: form.invoiceDate || null,
          dueDate: form.dueDate || null,
          serviceDate: form.serviceDate || null,
          serviceEndDate: form.serviceEndDate || null,
          paymentTerms: termsDays === 0 ? 'Due on invoice date' : `Net ${termsDays}`,
          clientName: form.clientName || '',
          clientPhone: form.clientPhone || '',
          clientEmail: form.clientEmail || '',
          clientAddress: form.clientAddress || '',
          attention: form.attention || '',
          projectAddress: form.projectAddress || '',
          deliveryAddress: form.deliveryAddress || '',
          purchaseOrderNumber: form.purchaseOrderNumber || '',
          bankName: form.bankName || '',
          accountName: form.accountName || '',
          bsb: form.bsb || '',
          accountNumber: form.accountNumber || '',
          bankReference: form.bankReference || '',
          lines: linesToSend,
        },
      });
      const row = saved?.createCustomInvoiceForProject;
      toast.success(
        editing
          ? 'Custom invoice updated'
          : `Custom invoice ${row?.invoiceNumber || ''} created`,
      );
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm());
      if (!createOnly) await fetchCustomInvoices();
      bumpRefresh();
    } catch (err) {
      toast.error(err?.message || 'Failed to save custom invoice');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const patchTask = (lineIndex, taskIndex, patch) => {
    setForm((prev) => ({
      ...prev,
      lines: (prev.lines || []).map((row, i) =>
        i === lineIndex
          ? {
              ...row,
              tasks: (row.tasks || []).map((item, j) =>
                j === taskIndex ? { ...item, ...patch } : item,
              ),
            }
          : row,
      ),
    }));
  };

  const removeTask = (lineIndex, taskIndex) => {
    setForm((prev) => ({
      ...prev,
      lines: (prev.lines || [])
        .map((row, i) =>
          i === lineIndex
            ? {
                ...row,
                tasks: (row.tasks || []).filter((_, j) => j !== taskIndex),
              }
            : row,
        )
        .filter((row) => (row.tasks || []).length),
    }));
  };

  /** Service from the catalog (tier → task groups → tasks) as one invoice line. */
  const addCatalogService = (pick) => {
    const byTask = Boolean(pick.pricedByTasks);
    const tasksFromPick = (pick.tasks || []).map((t) => ({
      taskId: '',
      groupName: String(t.groupName || '').trim(),
      title: t.title || '',
      scope: t.scope || '',
      description: t.scope || '',
      price: byTask && Number(t.price) > 0 ? String(t.price) : '',
    }));
    // Tier / package price: one priced row, the tasks underneath are included.
    const packageRow =
      !byTask && Number(pick.unitPrice) > 0
        ? [{
            taskId: '',
            groupName: '',
            title: pick.tierName || pick.name,
            description: '',
            price: String(pick.unitPrice),
          }]
        : [];
    setForm((prev) => ({
      ...prev,
      lines: [
        ...(prev.lines || []),
        {
          serviceId: pick.catalogId,
          serviceName: pick.name,
          tasks: [...packageRow, ...tasksFromPick],
        },
      ],
    }));
    setShowCatalogPicker(false);
  };

  const addTask = () => {
    const task = {
      taskId: '',
      title: '',
      groupName: '',
      scope: '',
      instructions: '',
      description: '',
      price: '',
    };
    setForm((prev) => {
      const lines = [...(prev.lines || [])];
      if (!lines.length) {
        lines.push({ serviceId: '', serviceName: 'Tasks', tasks: [task] });
      } else {
        const last = lines[lines.length - 1];
        lines[lines.length - 1] = {
          ...last,
          tasks: [...(last.tasks || []), task],
        };
      }
      return { ...prev, lines };
    });
  };

  const pricedTotal = taskPriceTotal(form.lines);

  const openSend = (invoice) => {
    setEmailTarget(invoice);
    setEmailNote({
      to: invoice.listCustomerEmail || clientEmail || '',
      ...invoiceEmailDraft(invoice.invoiceNumber),
      cc: '',
      dueDate: addDaysToDateInput(new Date(), 15),
    });
  };

  const handleSend = async () => {
    const to = String(emailNote.to || '').trim();
    if (!to) {
      toast.error('Enter the client email');
      return;
    }
    if (!emailNote.dueDate) {
      toast.error('Select the payment due date');
      return;
    }
    if (!emailTarget?.id) return;
    try {
      setSending(true);
      const savedSig = getLocalEmailSignature();
      await graphqlRequest(
        SEND_INVOICE_EMAIL,
        {
          bookingId,
          info: JSON.stringify({
            ...emailNote,
            signature: isBlankSignatureHtml(savedSig) ? undefined : savedSig,
          }),
          invoiceId: emailTarget.id,
        },
        { timeout: 120000 },
      );
      toast.success(
        String(emailTarget.invoiceStage || '').toUpperCase() === 'SENT'
          ? 'Custom invoice resent'
          : 'Custom invoice sent',
      );
      setEmailTarget(null);
      await fetchCustomInvoices();
      bumpRefresh();
    } catch (err) {
      toast.error(err?.message || 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const canCreate = Boolean(bookingId && (projectId || projects.length));
  if (!canCreate) {
    if (createOnly) {
      return (
        <button
          type="button"
          className="pd-custom-create-btn"
          onClick={() =>
            toast.error('Create a project for this lead before a custom invoice.')
          }
        >
          + Create custom invoice
        </button>
      );
    }
    return (
      <p className="pd-muted">No linked deal — custom invoices unavailable.</p>
    );
  }

  return (
    <>
      {createOnly ? (
        <button
          type="button"
          className="pd-custom-create-btn"
          onClick={() => openForm(null)}
        >
          + Create custom invoice
        </button>
      ) : (
      <div className="pd-custom-invoice">
        <div className="pd-custom-invoice-head">
          <div>
            <h3>Custom invoice</h3>
            <p>A separate invoice. You can edit it and send it to the client.</p>
          </div>
          <button
            type="button"
            className="pd-custom-create-btn"
            onClick={() => openForm(null)}
          >
            + Create custom invoice
          </button>
        </div>

        {customInvoices.length ? (
          <table className="pd-custom-invoice-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Stage</th>
                <th>Total</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customInvoices.map((inv) => {
                const sent =
                  String(inv.invoiceStage || '').toUpperCase() === 'SENT';
                return (
                  <tr key={inv.id}>
                    <td>{inv.invoiceNumber}</td>
                    <td>{inv.invoiceStage || 'DRAFT'}</td>
                    <td>{formatAUD(inv.listTotal ?? 0)}</td>
                    <td>
                      <div className="pd-custom-invoice-actions">
                        <button
                          type="button"
                          className="pd-custom-btn"
                          onClick={() => loadForEdit(inv)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="pd-custom-btn pd-custom-btn--primary"
                          onClick={() => openSend(inv)}
                        >
                          {sent ? 'Resend' : 'Send'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="pd-empty-hint">No custom invoices yet.</p>
        )}
      </div>
      )}

      {showForm &&
        createPortal(
          <div
            className="pd-custom-overlay"
            onClick={() => !saving && setShowForm(false)}
            role="presentation"
          >
            <div
              className="pd-custom-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <header className="pd-custom-modal-header">
                <div>
                  <h2>
                    {editing ? 'Edit custom invoice' : 'Create custom invoice'}
                  </h2>
                </div>
                <button
                  type="button"
                  className="pd-custom-modal-close"
                  disabled={saving}
                  onClick={() => setShowForm(false)}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </header>
              <div className="pd-custom-modal-body">
                {projects.length ? (
                  <label className="pd-custom-field">
                    <span>Project</span>
                    <select
                      value={chosenProjectId}
                      onChange={(e) => {
                        const next = e.target.value;
                        setChosenProjectId(next);
                        if (!editing) applyProjectDefaults(next);
                      }}
                    >
                      <option value="">Select project</option>
                      {projects.map((row) => (
                        <option key={row.id} value={row.id}>
                          {[row.projectId, row.name].filter(Boolean).join(' — ') || 'Project'}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label className="pd-custom-field">
                  <span>Invoice title</span>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Invoice title"
                  />
                </label>
                <div className="pd-custom-dates">
                  <label className="pd-custom-field">
                    <span>Invoice number</span>
                    <input
                      type="text"
                      readOnly
                      value={editing?.invoiceNumber || 'Assigned when saved'}
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Project reference</span>
                    <input
                      type="text"
                      readOnly
                      value={
                        [form.projectReference, form.projectName]
                          .filter(Boolean)
                          .join(' — ') || '—'
                      }
                    />
                  </label>
                </div>
                <label className="pd-custom-field">
                  <span>Description</span>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Shown on the invoice."
                  />
                </label>
                <p className="pd-custom-section">Tasks</p>
                <p className="pd-muted">
                  Project tasks are optional. Remove any you do not want, or add
                  your own with a price and client-facing scope. Extra charges
                  already on an invoice are left off.
                </p>
                {(form.lines || []).length ? (
                  (form.lines || []).map((line, lineIndex) => (
                    <div key={`${line.serviceId || line.serviceName}-${lineIndex}`} className="pd-custom-tasks">
                      <strong>{line.serviceName || 'Tasks'}</strong>
                      {(line.tasks || []).map((task, taskIndex) => {
                        const groupName = String(task.groupName || '').trim();
                        const newGroup =
                          groupName &&
                          groupName !==
                            String(line.tasks[taskIndex - 1]?.groupName || '').trim();
                        return (
                        <React.Fragment key={`${task.taskId || 'new'}-${taskIndex}`}>
                        {newGroup ? (
                          <p className="pd-custom-section">{groupName}</p>
                        ) : null}
                        <div className="pd-custom-task">
                          <label className="pd-custom-field">
                            <span>Task group</span>
                            <input
                              type="text"
                              value={task.groupName || ''}
                              placeholder="e.g. Bathrooms"
                              onChange={(e) =>
                                patchTask(lineIndex, taskIndex, {
                                  groupName: e.target.value,
                                })
                              }
                            />
                          </label>
                          <label className="pd-custom-field">
                            <span>
                              Task{task.groupName ? ` · ${task.groupName}` : ''}
                            </span>
                            <input
                              type="text"
                              value={task.title}
                              onChange={(e) =>
                                patchTask(lineIndex, taskIndex, {
                                  title: e.target.value,
                                })
                              }
                            />
                          </label>
                          <label className="pd-custom-field">
                            <span>Client-facing scope</span>
                            <textarea
                              rows={2}
                              value={task.scope || ''}
                              onChange={(e) =>
                                patchTask(lineIndex, taskIndex, {
                                  scope: e.target.value,
                                })
                              }
                            />
                          </label>
                          <div className="pd-custom-task-price">
                            <label className="pd-custom-field">
                              <span>Price</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={task.price}
                                placeholder="0.00"
                                onChange={(e) =>
                                  patchTask(lineIndex, taskIndex, {
                                    price: e.target.value,
                                  })
                                }
                              />
                            </label>
                            <button
                              type="button"
                              className="pd-custom-btn"
                              onClick={() => removeTask(lineIndex, taskIndex)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                        </React.Fragment>
                        );
                      })}
                    </div>
                  ))
                ) : (
                  <p className="pd-muted">No tasks yet. Add one if this invoice needs line items.</p>
                )}
                <button type="button" className="pd-custom-btn" onClick={addTask}>
                  + Add task
                </button>
                <button
                  type="button"
                  className="pd-custom-btn"
                  onClick={() => setShowCatalogPicker(true)}
                >
                  + Add service
                </button>
                {showCatalogPicker
                  ? createPortal(
                      <QuotationServicePicker
                        bookingId={bookingId}
                        persist={false}
                        zIndex={20010}
                        onClose={() => setShowCatalogPicker(false)}
                        onAdded={addCatalogService}
                      />,
                      document.body,
                    )
                  : null}
                <label className="pd-custom-field">
                  <span>{pricedTotal > 0 ? 'Total from tasks' : 'Invoice amount'}</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    readOnly={pricedTotal > 0}
                    value={pricedTotal > 0 ? pricedTotal.toFixed(2) : form.amount}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, amount: e.target.value }))
                    }
                    placeholder="0.00"
                  />
                </label>
                <label className="pd-custom-field">
                  <span>GST</span>
                  <select
                    value={form.invoiceType}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        invoiceType: e.target.value,
                      }))
                    }
                  >
                    <option value="STANDARD">Add 10% GST on top</option>
                    <option value="GST_INCLUDED">GST included in amount</option>
                    <option value="GST_EXCLUDED">No GST</option>
                  </select>
                </label>
                <p className="pd-custom-section">Dates</p>
                <div className="pd-custom-dates">
                  <label className="pd-custom-field">
                    <span>Issue date</span>
                    <input
                      type="date"
                      value={form.invoiceDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          invoiceDate: e.target.value,
                          dueDate: addDaysToDateInput(
                            e.target.value,
                            Number(prev.paymentTermsDays) || 0,
                          ),
                        }))
                      }
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Due date</span>
                    <input
                      type="date"
                      value={form.dueDate}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, dueDate: e.target.value }))
                      }
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Payment terms (days)</span>
                    <input
                      type="number"
                      min="0"
                      value={form.paymentTermsDays}
                      onChange={(e) => {
                        const days = e.target.value;
                        setForm((prev) => ({
                          ...prev,
                          paymentTermsDays: days,
                          dueDate: addDaysToDateInput(
                            prev.invoiceDate,
                            Number(days) || 0,
                          ),
                        }));
                      }}
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Service date from</span>
                    <input
                      type="date"
                      value={form.serviceDate}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, serviceDate: e.target.value }))
                      }
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Service date to</span>
                    <input
                      type="date"
                      value={form.serviceEndDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          serviceEndDate: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <p className="pd-custom-section">Client details</p>
                <div className="pd-custom-dates">
                  <label className="pd-custom-field">
                    <span>Name</span>
                    <input
                      type="text"
                      value={form.clientName}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, clientName: e.target.value }))
                      }
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Phone</span>
                    <input
                      type="text"
                      value={form.clientPhone}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, clientPhone: e.target.value }))
                      }
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Email</span>
                    <input
                      type="email"
                      value={form.clientEmail}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, clientEmail: e.target.value }))
                      }
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Address</span>
                    <input
                      type="text"
                      value={form.clientAddress}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          clientAddress: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <label className="pd-custom-field">
                  <span>Attention</span>
                  <input
                    type="text"
                    value={form.attention}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, attention: e.target.value }))
                    }
                  />
                </label>
                <label className="pd-custom-field">
                  <span>Project address</span>
                  <input
                    type="text"
                    value={form.projectAddress}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        projectAddress: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="pd-custom-field">
                  <span>Delivery address</span>
                  <input
                    type="text"
                    value={form.deliveryAddress}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        deliveryAddress: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="pd-custom-field">
                  <span>PO number</span>
                  <input
                    type="text"
                    value={form.purchaseOrderNumber}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        purchaseOrderNumber: e.target.value,
                      }))
                    }
                  />
                </label>
                <p className="pd-custom-section">Bank details</p>
                <div className="pd-custom-dates">
                  <label className="pd-custom-field">
                    <span>Bank name</span>
                    <input
                      type="text"
                      value={form.bankName}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, bankName: e.target.value }))
                      }
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Account name</span>
                    <input
                      type="text"
                      value={form.accountName}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, accountName: e.target.value }))
                      }
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>BSB</span>
                    <input
                      type="text"
                      value={form.bsb}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, bsb: e.target.value }))
                      }
                    />
                  </label>
                  <label className="pd-custom-field">
                    <span>Account number</span>
                    <input
                      type="text"
                      value={form.accountNumber}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          accountNumber: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <label className="pd-custom-field">
                  <span>Reference</span>
                  <input
                    type="text"
                    value={form.bankReference}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, bankReference: e.target.value }))
                    }
                  />
                </label>
              </div>
              <footer className="pd-custom-modal-footer">
                <button
                  type="button"
                  className="pd-custom-btn"
                  disabled={saving}
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pd-custom-btn pd-custom-btn--primary"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {saving
                    ? 'Saving…'
                    : editing
                      ? 'Save changes'
                      : 'Create invoice'}
                </button>
              </footer>
            </div>
          </div>,
          document.body,
        )}

      {emailTarget &&
        createPortal(
          <div className="email-overlay">
            <div className="email-composer">
              <div className="email-composer-header">
                <span>
                  {String(emailTarget.invoiceStage || '').toUpperCase() ===
                  'SENT'
                    ? 'Resend'
                    : 'Send'}{' '}
                  custom invoice — {emailTarget.invoiceNumber}
                </span>
                <button
                  type="button"
                  className="email-close-btn"
                  onClick={() => setEmailTarget(null)}
                >
                  ✕
                </button>
              </div>
              <div className="email-row">
                <label>To</label>
                <input
                  type="email"
                  className="email-input"
                  value={emailNote.to}
                  onChange={(e) =>
                    setEmailNote((p) => ({ ...p, to: e.target.value }))
                  }
                />
              </div>
              <div className="email-row">
                <label>Subject</label>
                <input
                  type="text"
                  className="email-input"
                  value={emailNote.subject}
                  onChange={(e) =>
                    setEmailNote((p) => ({ ...p, subject: e.target.value }))
                  }
                />
              </div>
              <div className="email-row">
                <label>Payment due date</label>
                <input
                  type="date"
                  className="email-input"
                  value={emailNote.dueDate}
                  onChange={(e) =>
                    setEmailNote((p) => ({ ...p, dueDate: e.target.value }))
                  }
                />
              </div>
              <div className="email-row">
                <label>Message</label>
                <textarea
                  className="email-textarea"
                  value={emailNote.message}
                  onChange={(e) =>
                    setEmailNote((p) => ({ ...p, message: e.target.value }))
                  }
                />
              </div>
              <div className="email-composer-actions">
                <button
                  type="button"
                  className="cancel-email-btn"
                  onClick={() => setEmailTarget(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="send-email-btn"
                  disabled={sending}
                  onClick={handleSend}
                >
                  {sending
                    ? 'Sending…'
                    : String(emailTarget.invoiceStage || '').toUpperCase() ===
                        'SENT'
                      ? 'Resend'
                      : 'Send'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
